'use client';

import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/api/client';
import type { components } from '@/lib/api/generated/schema';

type CompleteOnboardingRequest =
  components['schemas']['CompleteOnboardingRequest'];
type CompleteOnboardingResponse =
  components['schemas']['CompleteOnboardingResponse'];

/**
 * OnboardingError carries the backend error code (e.g. "already_linked" for a 409)
 * alongside the human message, so the caller can branch into a recovery flow instead
 * of only showing text.
 */
export class OnboardingError extends Error {
  code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.name = 'OnboardingError';
    this.code = code;
  }
}

export function useCompleteOnboarding() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<
    CompleteOnboardingResponse,
    Error,
    Omit<CompleteOnboardingRequest, 'status'>
  >({
    mutationFn: async (data: Omit<CompleteOnboardingRequest, 'status'>) => {
      const token = await getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const { data: responseData, error } = await apiClient.PATCH(
        '/api/v1/onboarding',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: {
            ...data,
            status: 'completed',
          },
        }
      );

      if (error) {
        // The API returns { error: <code>, message: <human text> } (see the Go
        // onboarding handler's writeError). Surface that real message instead of a
        // generic string — otherwise causes like a 409 "already linked" are hidden.
        const e = error as {
          message?: string;
          error?: string;
          detail?: string;
        };
        throw new OnboardingError(
          e.message || e.detail || e.error || 'Failed to complete onboarding',
          e.error
        );
      }

      return responseData as CompleteOnboardingResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'status'] });
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      queryClient.invalidateQueries({ queryKey: ['members'] });
    },
  });
}
